import {Children, FC, ReactNode, cloneElement, useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import Media from 'react-media';
import {Transition, TransitionStatus} from 'react-transition-group';
import {createPortal} from 'react-dom';

import Icon, {Icons} from '~/common/Icon';
import useMediaQuery from '~/hooks/useMediaQuery';
import {isMobileDevice} from '~/utils/browserDetect';
import useWindowLandscape from '~/hooks/useWindowLandscape';
import {useSelector} from '~/store/hooks';

import BodyNoScroll from '../ProfileCard/BodyNoScroll';
import s from './styles.module.scss';

const queries = {
  small: '(max-width: 767px)',
  large: '(min-width: 768px)',
};

const transformDuration = 150;

const defaultStyle = {
  transition: `transform ${transformDuration}ms ease-out`,
  position: 'relative',
};

const transitionStyles: {[key in TransitionStatus]: any} = {
  entering: {
    transform: 'translateX(0)',
  },
  entered: {
    transform: 'translateX(0)',
  },
  exiting: {
    transform: 'translateX(360px)',
  },
  exited: {
    transform: 'translateX(360px)',
  },
  unmounted: {},
};

interface DropdownProps {
  children: ReactNode;
  classes?: {
    wrapper?: string;
    childWrapper?: string;
  };
}

export const Dropdown: FC<DropdownProps> = ({children, classes = {}}) => {
  const {isSnackbarVisible} = useSelector(({app}) => app);
  const [isOpen, setIsOpen] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isLandscape = useWindowLandscape();

  useEffect(() => {
    if (!isOpen) return;

    const handler = (event: any) => {
      event.preventDefault();
      setIsOpen(false);
    };

    document.addEventListener('click', handler);

    return () => {
      document.removeEventListener('click', handler);
    };
  }, [isOpen]);

  useEffect(() => {
    let touchStart: any;
    let touchEnd: any;

    const diff = 45;

    const touchStartHandler = (e: any) => {
      touchStart = e.targetTouches[0];
      touchEnd = null;
    };

    const touchMoveHandler = (e: any) => {
      touchEnd = e.targetTouches[0];
    };

    const handleTouchEnd = () => {
      if (!touchStart || !touchEnd || !nodeRef.current) return;
      const halfWidth = 300 / 2;
      const xDiff = touchEnd.clientX - touchStart.clientX;
      const yDiff =
        Math.max(touchStart.clientY, touchEnd.clientY) -
        Math.min(touchStart.clientY, touchEnd.clientY);

      if (xDiff > yDiff && touchStart.clientX <= halfWidth) {
        if (xDiff > diff) {
          setIsOpen(false);
        }
      }

      touchStart = null;
      touchEnd = null;
    };

    if (nodeRef.current) {
      const sidebar = nodeRef.current;

      sidebar.addEventListener('touchstart', touchStartHandler);
      sidebar.addEventListener('touchmove', touchMoveHandler);
      sidebar.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      if (nodeRef.current) {
        nodeRef.current.removeEventListener('touchstart', touchStartHandler);
        nodeRef.current.removeEventListener('touchmove', touchMoveHandler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        nodeRef.current.removeEventListener('touchend', handleTouchEnd);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeRef.current]);

  // Prevent click on dropdown from closing it
  const handleClick = (e: any) => {
    e.stopPropagation();
  };

  const getContent = () => {
    // Hack to force rerender to calc checkSnakbarVisibility
    const clonedChildren = Children.map(children, (child) => {
      return cloneElement(child, {
        isSnackbarVisible,
      });
    });

    return (
      <div
        className={clsx(
          s['child-wrapper'],
          {
            [s['child-wrapper__opened']]: isOpen,
            [s['child-wrapper__opened--shifted']]: isSnackbarVisible,
            [s['child-wrapper__opened--landscape']]: isLandscape && isMobileDevice(),
          },
          classes.childWrapper
        )}
        onClick={handleClick}
      >
        <Media queries={queries}>
          {(matches) => (
            <>
              {matches.small && (
                <Transition nodeRef={nodeRef} in={isOpen} timeout={500}>
                  {(state) => (
                    <div
                      ref={nodeRef}
                      style={{
                        ...defaultStyle,
                        ...transitionStyles[state],
                      }}
                    >
                      <BodyNoScroll isOpen={isOpen}>{clonedChildren}</BodyNoScroll>
                    </div>
                  )}
                </Transition>
              )}
              {matches.large && (
                <div style={{display: isOpen ? 'flex' : 'none'}}>{clonedChildren}</div>
              )}
            </>
          )}
        </Media>
      </div>
    );
  };

  return (
    <div id="app-menu-dropdown" className={clsx(s.wrapper, classes.wrapper)}>
      <Icon
        name={Icons.menu}
        onClick={(e) => {
          setIsOpen((v) => !v);
          e.preventDefault();
          e.stopPropagation();

          return false;
        }}
        className={clsx('icon-menu', s['dropdown-menu-icon'])}
      />
      {isMobile ? createPortal(getContent(), document.body) : getContent()}
    </div>
  );
};

export default Dropdown;
